import { datadogRum } from '@datadog/browser-rum';
import { reactPlugin } from '@datadog/browser-rum-react';

import { getRawTunerVarsData } from './configuration/TunerVarUtils';

export const ERROR_TYPE_API = 'api';
export const ERROR_TYPE_UI = 'ui';

// Datadog client config
const DATADOG_CLIENT_CONFIG = {
  applicationId: '2c8664f9-0282-4fe7-9504-15f6bccaceeb',
  clientToken: 'pub16b313e1658347a16504b662a893cd7e',
  env: __RELEASE_STAGE__,
  service: 'pandora-web',
  site: 'datadoghq.com',
  version: __VERSION__,
};

interface DatadogConfig {
  datadogRUMEnabled?: boolean;
  sessionReplaySampleRate?: number;
  sessionSampleRate?: number;
  trackLongTasks?: boolean;
  trackResources?: boolean;
  trackUserInteractions?: boolean;
}

let isInitialized = false;

export function resetDatadogForTesting() {
  isInitialized = false;
}

// Default Datadog configuration (temporary until runtime config is available)
const DEFAULT_DATADOG_CONFIG: DatadogConfig = {
  datadogRUMEnabled: true,
  sessionReplaySampleRate: 10,
  sessionSampleRate: 100,
  trackLongTasks: true,
  trackResources: true,
  trackUserInteractions: true,
};

/**
 * Initialize Datadog RUM
 */
export function initDatadog() {
  if (isInitialized || __RELEASE_STAGE__ === 'development') {
    return;
  }

  const rawTunerVars = getRawTunerVarsData();

  // default to enabled (temporary until runtime config is available)
  const webDatadogEnabled = rawTunerVars?.webDatadogEnabled || true;

  const config: DatadogConfig = {
    ...DEFAULT_DATADOG_CONFIG,
    ...rawTunerVars?.webDatadogConfig,
  };

  if (!webDatadogEnabled || !config.datadogRUMEnabled) {
    return;
  }

  try {
    datadogRum.init({
      applicationId: DATADOG_CLIENT_CONFIG.applicationId,
      clientToken: DATADOG_CLIENT_CONFIG.clientToken,
      site: DATADOG_CLIENT_CONFIG.site,
      service: DATADOG_CLIENT_CONFIG.service,
      env: DATADOG_CLIENT_CONFIG.env,
      version: DATADOG_CLIENT_CONFIG.version,
      sessionSampleRate: config.sessionSampleRate ?? 100,
      sessionReplaySampleRate: config.sessionReplaySampleRate ?? 10,
      trackUserInteractions: config.trackUserInteractions ?? true,
      trackResources: config.trackResources ?? true,
      trackLongTasks: config.trackLongTasks ?? true,
      defaultPrivacyLevel: 'mask-user-input',
      plugins: [reactPlugin({ router: false })],
    });

    isInitialized = true;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to initialize Datadog RUM:', error);
  }
}

/**
 * Check if Datadog is enabled
 */
export function isDatadogEnabled(): boolean {
  return isInitialized && !!datadogRum.getInternalContext();
}

/**
 * Set user context
 */
export function setDatadogUser(userId: string, userAttributes?: Record<string, unknown>) {
  if (!isDatadogEnabled()) {
    return;
  }

  try {
    datadogRum.setUser({
      id: userId,
      ...userAttributes,
    });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to set Datadog user:', error);
  }
}

/**
 * Add custom context
 */
export function setDatadogContext(key: string, value: unknown) {
  if (!isDatadogEnabled()) {
    return;
  }

  try {
    datadogRum.setGlobalContextProperty(key, value);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to set Datadog context:', error);
  }
}

/**
 * Add tags
 */
export function setDatadogTag(key: string, value: string | boolean | number) {
  setDatadogContext(key, value);
}

/**
 * Log an error
 */
export function logDatadogError(message: string, context?: Record<string, unknown>, error?: Error) {
  if (!isDatadogEnabled()) {
    return;
  }

  try {
    datadogRum.addError(error || new Error(message), {
      message,
      ...context,
    });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('Failed to log error to Datadog:', err);
  }
}

/**
 * Clear user context
 */
export function clearDatadogUser() {
  if (!isDatadogEnabled()) {
    return;
  }

  try {
    datadogRum.clearUser();
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to clear Datadog user:', error);
  }
}
